import { ReactElement, useMemo, useState } from 'react'

import { useTheme } from '@emotion/react'
import styled from '@emotion/styled'
import { useTranslation } from 'react-i18next'
import Select, {
  components,
  createFilter,
  DropdownIndicatorProps,
  MultiValue,
  MultiValueRemoveProps,
} from 'react-select'

import { ExpandIcon, NavigationCloseIcon } from '@arcade/web/icons/react'

import { createLegacyMenuComponent, useMenuCloseControl } from './LegacyReactSelectMenu'

export type DropdownInputProps<T> = {
  autoFocus?: boolean
  options: T[]
  formValue: T[] | null
  handleChange: (options: T[] | null, action: string, onChange?: (value: MultiValue<T> | null) => void) => void
  field?: { onChange: (value: MultiValue<T> | null) => void }
  placeholder?: string
  ariaLabel?: string
  className?: string
  isError?: boolean
}

const DropdownIndicator = <T,>(props: DropdownIndicatorProps<T, true>) => {
  return (
    <components.DropdownIndicator {...props}>
      <ExpandIcon size={24} />
    </components.DropdownIndicator>
  )
}

const MultiValueRemoveIcon = styled(NavigationCloseIcon)`
  padding-top: 0.125rem;
  ${props => props.theme.motion.standardTransition}
  transition-property: opacity;

  &:hover {
    opacity: 0.75;
  }

  &:active {
    opacity: 0.5;
  }
`

const MultiValueRemove = (props: MultiValueRemoveProps) => {
  return (
    <components.MultiValueRemove {...props}>
      <MultiValueRemoveIcon size={24} />
    </components.MultiValueRemove>
  )
}

const NoOptionsContent = styled.div`
  ${props => props.theme.typography.input}
  padding: 0.75rem 1rem;
`

const NoOptionsMessage = () => {
  const { t } = useTranslation()

  return <NoOptionsContent> {t('No option found', { context: 'MultiDropdown - No options message' })}</NoOptionsContent>
}

export const MultiDropdown = <
  T extends {
    inputLabel: ReactElement
    labelCopy: string
  },
>({
  autoFocus,
  options,
  formValue,
  handleChange,
  field,
  placeholder,
  ariaLabel,
  className,
  isError = false,
}: DropdownInputProps<T>) => {
  const theme = useTheme()
  const [isInputFocused, setIsInputFocused] = useState(false)
  const { isMenuOpen, setIsMenuOpen, controlRef } = useMenuCloseControl()
  const MenuComponent = useMemo(() => createLegacyMenuComponent<T, true>(controlRef), [controlRef])

  const SelectWithType = Select<T, true>

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    // When the Escape key is pressed and the menu is open, stop propagation
    // This prevents the modal from closing when a dropdown inside it is closed with ESC
    if (event.key === 'Escape' && isMenuOpen) {
      event.stopPropagation()
    }
  }

  return (
    <div ref={controlRef}>
      <SelectWithType
        {...field}
        onFocus={() => setIsInputFocused(true)}
        onBlur={() => setIsInputFocused(false)}
        value={formValue ? formValue : null} // If we want to clear the value, we must use `null`
        onChange={(option, { action }) => handleChange(Array.from(option ?? []), action, field?.onChange)}
        autoFocus={autoFocus}
        aria-label={ariaLabel}
        minMenuHeight={0}
        placeholder={placeholder}
        options={options}
        onKeyDown={handleKeyDown}
        onMenuOpen={() => setIsMenuOpen(true)}
        onMenuClose={() => setIsMenuOpen(false)}
        menuIsOpen={isMenuOpen}
        isMulti={true}
        components={{
          DropdownIndicator,
          MultiValueLabel: ({ data }: { data: T }) => data.inputLabel,
          MultiValueRemove,
          ClearIndicator: () => null,
          NoOptionsMessage,
          Menu: MenuComponent,
        }}
        unstyled
        // override the default behavior where it filters by value and not the label shown in the UI
        filterOption={createFilter({
          matchFrom: 'any',
          stringify: option => `${option.data.labelCopy}`,
        })}
        className={className}
        styles={{
          container: base => {
            return {
              ...base,
              fontSize: '1rem',
              lineHeight: '1.375rem',
              fontWeight: theme.weights.regular,
              caretColor: theme.colors.primary,
              color: theme.colors.body,
            }
          },
          menuPortal: base => ({ ...base, zIndex: 99999 }),
          dropdownIndicator: base => ({ ...base, paddingRight: '0.25rem' }),

          control: (base, props) => {
            const { isFocused } = props

            let stateSpecificStyles = {}
            if (isFocused) {
              stateSpecificStyles = {
                ...stateSpecificStyles,
                boxShadow: `0 0 0 0.125rem ${isError ? theme.colors.error : theme.colors.primary} inset`,
              }
            } else {
              stateSpecificStyles = {
                ...stateSpecificStyles,
                backgroundColor: theme.colors.formFieldBackground,
                boxShadow: `0 0 0 0.125rem ${isError ? theme.colors.error : theme.colors.formFieldBackground} inset`,
                cursor: 'pointer',
                overflow: 'hidden',
              }
            }

            return {
              ...base,
              borderRadius: '1rem',
              padding: '0.75rem',
              outline: 'none',
              transition: 'none',
              cursor: 'pointer',
              ...stateSpecificStyles,
            }
          },
          input: base => {
            return {
              ...base,
              lineHeight: '1.125rem',
              fontSize: '1.125rem',
              color: theme.colors.label,
              marginLeft: '0.375rem',
            }
          },

          menu: base => {
            return {
              ...base,
              backgroundColor: theme.colors.background,
              border: `1px solid ${theme.colors.outline}`,
              borderRadius: '0.75rem',
              overflow: 'hidden',
              top: 'calc(100% + 0.5rem)',
              boxShadow: '0px 12px 24px rgba(0, 0, 0, 0.04)',
            }
          },
          menuList: base => {
            return {
              ...base,
              maxHeight: '16rem',
            }
          },
          multiValue: base => {
            let stateSpecificStyles = {}
            if (isInputFocused) {
              stateSpecificStyles = {
                ...stateSpecificStyles,
                backgroundColor: theme.colors.formFieldBackground,
              }
            } else {
              stateSpecificStyles = {
                ...stateSpecificStyles,
                backgroundColor: theme.colors.background,
                boxShadow: theme.dropShadows.topBar,
              }
            }

            return {
              ...base,
              ...stateSpecificStyles,
              alignItems: 'center',
              borderRadius: '0.375rem',
              margin: '0.25rem',
              color: theme.colors.label,
              display: 'flex',
              lineHeight: '1.5rem',
              padding: '0.5rem 0.5rem 0.5rem 0.75rem',
              cursor: 'default',
            }
          },
          multiValueRemove: base => {
            return {
              ...base,
              cursor: 'pointer',
              marginLeft: '0.5rem',
              color: theme.colors.tertiaryLabel,
              width: '1rem',
            }
          },
          option: (base, props) => {
            const { isFocused } = props

            let stateSpecificStyles = {}
            if (isFocused) {
              stateSpecificStyles = {
                ...stateSpecificStyles,
                backgroundColor: theme.colors.behindBackground,
              }
            }

            return {
              ...base,
              borderBottom: `0.0625rem solid ${theme.colors.outline}`,
              cursor: 'pointer',
              ...stateSpecificStyles,

              '&:last-child': {
                borderBottom: 'none',
              },
            }
          },
          placeholder: base => {
            return {
              ...base,
              lineHeight: '1.5rem',
              fontSize: '1.125rem',
              color: theme.colors.placeholderLabel,
              marginLeft: '0.375rem',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              position: 'absolute',
            }
          },
          valueContainer: base => {
            return {
              ...base,
              display: 'flex',
              flexWrap: 'wrap',
              fontSize: '1.125rem',
              color: theme.colors.label,
              minHeight: '2.5rem',
            }
          },
        }}
      />
    </div>
  )
}
